import {Store}               from '../index';
import {Injectable}          from '@angular/core';
import {
  ApCustomTypes,
  NetTypes,
  NFertilizationLogfilesRemapComplete,
  PlantProtectionLogfilesBook,
  PlantProtectionLogfilesLoad,
  PlantProtectionLogfilesLoadSuccess,
  PlantProtectionLogfilesRemap,
  PlantProtectionLogfilesUnBook,
  PlantProtectionLogfilesUpdate,
  PlantProtectionPlanBooksLoad,
  PlantProtectionPlanBooksLoadFail,
  PlantProtectionPlanBooksLoadSuccess,
  PlantProtectionPlanBooksSave,
  PlantProtectionSummariesDelete
}                            from 'invoker-transport';
import {IStateStore}         from '../../ap-interface';
import {
  PlantProtectionPlanBookSet,
  PPPlanningRasterCreateJob,
  PPPlanningRasterStatisticFail,
  PPPlanningRasterStatisticRefresh
}                            from '../../../../projects/invoker-transport/src/lib/actions/plant-protection';
import {SafeBehaviorSubject} from 'ts-tooling';
import {ApSignalrService}    from '../../ap-core/services/ap-signalr.service';
import IGuid = System.IGuid;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import IPlantProtectionLogEdit = Data.PlantProtection.IPlantProtectionLogEdit;
import INPlanningData = Data.NFertilization.INPlanningData;
import IFieldNutrientPlanningStatistic = Data.Nutrients.IFieldNutrientPlanningStatistic;
import IRasterCreationResult = Data.Nutrients.IRasterCreationResult;

interface IPlantProtectionPlanBookStore extends IStateStore<IPlantProtectionPlanBook> {
  logfiles: IDatasetFieldMap[];
  logfilesLoading: boolean;
  fieldPPPlanningStatistic: IFieldNutrientPlanningStatistic[];
}

@Injectable({providedIn: 'root'})
export class PlantProtectionPlanBookStore extends Store<IPlantProtectionPlanBookStore> {
  constructor(public backend: ApSignalrService) {
    super(backend, {
      data: [],
      logfiles: [],
      logfilesLoading: false,
      loaded: false,
      loading: false,
      fieldPPPlanningStatistic: []
    });
    backend.registerObservable(PlantProtectionPlanBooksLoadSuccess).subscribe(d => {
      super.Mutate(s => s.data, () => d.Data);
      super.SetLoadFinishState();
    });
    backend.registerObservable(PlantProtectionPlanBooksLoadFail).subscribe(() => {
      super.Mutate(s => s.data, () => []);
      super.SetLoadFinishState();
    });
    backend.registerObservable(PlantProtectionLogfilesLoadSuccess).subscribe((d) => {
      super.Mutate((s) => s.logfiles, () => d.Data);
      super.Mutate((s) => s.logfilesLoading, () => false);
    });
    backend.registerObservable(NFertilizationLogfilesRemapComplete).subscribe(() => {
      this.loadLogfiles();
    });
    backend.registerObservable(PPPlanningRasterStatisticRefresh).subscribe(d => {
      const statisticsToAdd = JSON.parse(d.Data) as IFieldNutrientPlanningStatistic[];
      super.Mutate(s => s.fieldPPPlanningStatistic, o => {
        if (o.Find(i => i?.FieldGeomId === statisticsToAdd[0]?.FieldGeomId)) {
          o = o.RemoveAll(i => i.FieldGeomId === statisticsToAdd[0].FieldGeomId);
          o = o.AddRange(statisticsToAdd);
        } else {
          o = o.AddRange(statisticsToAdd);
        }
        return o;
      });
    });
    backend.registerObservable(PPPlanningRasterStatisticFail).subscribe(d => {
      const result = JSON.parse(d.Data) as IRasterCreationResult;
      super.Mutate(s => s.fieldPPPlanningStatistic, o => {
        if (o.Find(i => i?.FieldGeomId === result?.Id)) {
          const stat = o.Find(i => i?.FieldGeomId === result?.Id)[0];
          stat.ErrorText = result.Message;
          o = o.Replace(i => i.FieldGeomId === result.Id, stat);
        } else {
          const err = {FieldGeomId: result.Id, ErrorText: result.Message} as IFieldNutrientPlanningStatistic;
          o = o.Add(err);
        }
        return o;
      });
    });
  }

  public get Loading$(): SafeBehaviorSubject<boolean> {
    return super.Listen(s => s.loading);
  }

  public get PlanBooks$(): SafeBehaviorSubject<IPlantProtectionPlanBook[]> {
    return this.Listen((s) => s.data);
  }

  public get PlanBooks(): IPlantProtectionPlanBook[] {
    return this.PlanBooks$.getValue();
  }

  public getLogfilesByPlanBookId(id: string | IGuid): IDatasetFieldMap[] {
    return super.Listen(s => s.logfiles).getValue().filter(l => l.PlantProtectionPlanBook?.Id === id);
  }

  public loadPlanBooks(): void {
    super.SetLoadState();
    super.Mutate(s => s.data, () => []);
    this.DispatchBackend(new PlantProtectionPlanBooksLoad([]));
  }

  public getPlanningsBySummaryId(summaryId: string | IGuid): IPlantProtectionPlanBook[] {
    return this.Listen(s => s.data).getValue().filter((pB) => pB.Summary?.Id === summaryId);
  }

  public savePlanBooks(planBooks: IPlantProtectionPlanBook[]): void {
    this.DispatchBackend(new PlantProtectionPlanBooksSave([
      {Name: 'planBooks', Type: ApCustomTypes.Data_TaskManagement_PlantProtectionPlanBook + '[]', Value: planBooks}
    ]));
  }

  public setPlanningToLogfile(logEdit: IPlantProtectionLogEdit): void {
    this.DispatchBackend(new PlantProtectionPlanBookSet([
      {Name: 'logEdit', Type: ApCustomTypes.Data_PlantProtection_PlantProtectionLogEdit, Value: logEdit},
    ]));
  }

  public loadLogfiles(): void {
    this.Mutate(s => s.logfilesLoading, () => true);
    this.DispatchBackend(new PlantProtectionLogfilesLoad([]));
  }

  public updateLogfiles(datasetFieldMaps: IDatasetFieldMap[]): void {
    this.DispatchBackend(new PlantProtectionLogfilesUpdate([
      {Name: 'logfiles', Type: ApCustomTypes.Data_Sensor_DatasetFieldMap + '[]', Value: datasetFieldMaps}
    ]));
  }

  public remapLogfile(datasetFieldMaps: IDatasetFieldMap | IDatasetFieldMap[]): void {
    if (!Array.isArray(datasetFieldMaps)) {
      datasetFieldMaps = [datasetFieldMaps];
    }
    const dataSets = datasetFieldMaps.map(x => x.DataSet);
    this.DispatchBackend(new PlantProtectionLogfilesRemap([
      {Name: 'dataSets', Type: ApCustomTypes.Data_Import_DataSet + '[]', Value: dataSets}
    ]));
  }

  public bookPlanBooks(data: { planBook: IPlantProtectionPlanBook, logFile: IDatasetFieldMap }[]): void {
    const planBooks = data.map(x => x.planBook);
    const logFiles = data.map(x => x.logFile);
    this.DispatchBackend(new PlantProtectionLogfilesBook([
      {Name: 'planBooks', Type: ApCustomTypes.Data_TaskManagement_PlantProtectionPlanBook + '[]', Value: planBooks},
      {Name: 'logFiles', Type: ApCustomTypes.Data_Sensor_DatasetFieldMap + '[]', Value: logFiles}
    ]));
  }

  public unBookPlanBooks(data: { planBook: IPlantProtectionPlanBook, logFile: IDatasetFieldMap }[]): void {
    const planBooks = data.map(x => x.planBook);
    const logFiles = data.map(x => x.logFile);
    this.DispatchBackend(new PlantProtectionLogfilesUnBook([
      {Name: 'planBooks', Type: ApCustomTypes.Data_TaskManagement_PlantProtectionPlanBook + '[]', Value: planBooks},
      {Name: 'logFiles', Type: ApCustomTypes.Data_Sensor_DatasetFieldMap + '[]', Value: logFiles}
    ]));
  }

  public deleteSummaries(data: IGuid[]): void {
    this.DispatchBackend(new PlantProtectionSummariesDelete([
      {Name: 'summaryIds', Type: NetTypes.GUID + '[]', Value: data}
    ]));
  }

  public CreatePPPlanningRasterJob(planningData: INPlanningData): void {
    super.Mutate(s => s.fieldPPPlanningStatistic, o => {
      return o.RemoveAll(i => i.FieldGeomId === planningData.FieldGeomId);
    });
    this.DispatchBackend(new PPPlanningRasterCreateJob([
      {Name: 'planningData', Type: ApCustomTypes.Data_NFertilization_NPlanningData, Value: planningData}
    ]));
  }

  public clearPPPlanningStatistic(): void {
    super.Mutate(s => s.fieldPPPlanningStatistic, () => []);
  }
}
